import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export function StoreProvider({ children }) {
  const sessionId = getSessionId();
  const [cart, setCart] = useState({ items: [], totals: { subtotal: 0, discount: 0, delivery: 0, total: 0 } });
  const [wishlist, setWishlist] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const refreshCart = useCallback(async () => {
    const { data } = await api.get(`/cart/${sessionId}`);
    setCart(data);
  }, [sessionId]);

  const refreshWishlist = useCallback(async () => {
    const { data } = await api.get(`/wishlist/${sessionId}`);
    setWishlist(data.product_ids || []);
  }, [sessionId]);

  useEffect(() => {
    refreshCart();
    refreshWishlist();
  }, [refreshCart, refreshWishlist]);

  const addToCart = async (product, qty = 1, openDrawer = true) => {
    const { data } = await api.post(`/cart/add`, { session_id: sessionId, product_id: product.id, qty });
    setCart(data);
    toast.success(`${product.name} added to cart`, { description: `${qty} × ₹${product.price}` });
    if (openDrawer) setCartOpen(true);
  };

  const setQty = async (productId, qty) => {
    const { data } = await api.post(`/cart/set`, { session_id: sessionId, product_id: productId, qty });
    setCart(data);
  };

  const clearCart = async () => {
    const { data } = await api.post(`/cart/clear`, { session_id: sessionId });
    setCart(data);
  };

  const toggleWishlist = async (product) => {
    const { data } = await api.post(`/wishlist/toggle`, { session_id: sessionId, product_id: product.id });
    setWishlist(data.product_ids);
    const active = data.product_ids.includes(product.id);
    toast[active ? "success" : "message"](active ? "Added to wishlist" : "Removed from wishlist");
  };

  const cartCount = cart.items.reduce((s, i) => s + i.qty, 0);

  const value = {
    sessionId, cart, cartCount, wishlist, cartOpen, setCartOpen,
    searchOpen, setSearchOpen, addToCart, setQty, clearCart,
    toggleWishlist, refreshCart, refreshWishlist,
    isWishlisted: (id) => wishlist.includes(id),
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
